export const LLM_MODEL_AUTO = 'auto';

/**
 * Dispatched on `globalThis` whenever the stored model changes, so open pickers
 * re-sync when the model is switched programmatically (e.g. accepting a model
 * suggestion) rather than through the picker itself. Detail carries the target
 * conversationId (`null` for the global default) and the new model.
 */
export const LLM_MODEL_CHANGED_EVENT = 'aibuild:llm-model-changed';

export interface LlmModelChangedDetail {
  conversationId: string | null;
  model: string;
}

const LLM_MODEL_STORAGE_KEY = 'aibuild_llm_model';
const LLM_MODEL_DEFAULT_KEY = 'aibuild_llm_model_default';
const DISMISSED_MODEL_SUGGESTIONS_KEY = 'aibuild_dismissed_model_suggestions';

export const getStoredLlmModel = (conversationId: string | null): string => {
  if (conversationId) {
    try {
      const stored = localStorage.getItem(LLM_MODEL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const value = parsed[conversationId];
        if (typeof value === 'string' && value) return value;
      }
    } catch {
      // Fall through to global default
    }
  }
  try {
    return localStorage.getItem(LLM_MODEL_DEFAULT_KEY) || LLM_MODEL_AUTO;
  } catch {
    return LLM_MODEL_AUTO;
  }
};

export const setStoredLlmModel = (conversationId: string | null, model: string): void => {
  // Always update the global default so new conversations inherit the last choice
  try {
    localStorage.setItem(LLM_MODEL_DEFAULT_KEY, model);
  } catch {
    // Ignore storage errors
  }
  try {
    if (conversationId) {
      const stored = localStorage.getItem(LLM_MODEL_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : {};
      parsed[conversationId] = model;
      localStorage.setItem(LLM_MODEL_STORAGE_KEY, JSON.stringify(parsed));
    }
  } catch {
    // Ignore storage errors
  }
  try {
    globalThis.dispatchEvent?.(
      new CustomEvent<LlmModelChangedDetail>(LLM_MODEL_CHANGED_EVENT, {
        detail: { conversationId: conversationId ?? null, model },
      }),
    );
  } catch {
    // Ignore environments without CustomEvent
  }
};

/**
 * Task types the user dismissed a model-swap suggestion for in a conversation
 * (AIB-7877). Forwarded on the next send so the gate never re-fires a dismissed
 * suggestion. Scoped per conversationId; the global default never dismisses.
 */
export const getDismissedModelSuggestions = (conversationId: string | null): string[] => {
  if (!conversationId) return [];
  try {
    const stored = localStorage.getItem(DISMISSED_MODEL_SUGGESTIONS_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    const value = parsed[conversationId];
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

export const addDismissedModelSuggestion = (
  conversationId: string | null,
  taskType: string,
): void => {
  if (!conversationId || !taskType) return;
  try {
    const stored = localStorage.getItem(DISMISSED_MODEL_SUGGESTIONS_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    const existing: string[] = Array.isArray(parsed[conversationId]) ? parsed[conversationId] : [];
    if (!existing.includes(taskType)) {
      parsed[conversationId] = [...existing, taskType];
      localStorage.setItem(DISMISSED_MODEL_SUGGESTIONS_KEY, JSON.stringify(parsed));
    }
  } catch {
    // Ignore storage errors
  }
};
